import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import { appendCardFilterParams } from 'store/services/cards/search/cardsSearchApi';
import type { SearchRequestArgs } from 'screens/Main/Search/searchState';
import {
  RecentActivityResponse,
  RecentActivityRequest,
  Activity,
  ActivityStats,
  AvailableFeedRequest,
  AvailableFeedResponse,
} from 'types/interfaces/activity';

/** Stable cache-key fragment for a filter set: the wire string itself. */
const filterKey = (filters?: SearchRequestArgs): string => {
  if (!filters) return '';
  const params = new URLSearchParams();
  appendCardFilterParams(params, filters);
  return params.toString();
};

export const activityApi = createApi({
  reducerPath: 'activityApi',
  baseQuery: baseQuery,
  tagTypes: ['RecentActivity', 'AvailableFeed'],
  endpoints: (builder) => ({
    getRecentActivity: builder.query<RecentActivityResponse, RecentActivityRequest>({
      query: ({ limit = 54, cursor } = {}) => {
        const params = new URLSearchParams();
        params.append('limit', String(limit));
        if (cursor) {
          params.append('cursor', cursor);
        }
        return {
          url: `/activity/recent?${params.toString()}`,
          method: 'GET',
          requiresAuth: false, // Public endpoint for social proof
        };
      },
      providesTags: ['RecentActivity'],
      keepUnusedDataFor: 60, // Cache for 60 seconds
    }),

    getActivityFeed: builder.query<RecentActivityResponse, RecentActivityRequest>({
      query: ({ limit = 30, cursor, traderBalanceMin, traderBalanceMax, filters } = {}) => {
        const params = new URLSearchParams();
        params.append('limit', String(limit));
        if (cursor) {
          params.append('cursor', cursor);
        }
        if (traderBalanceMin != null) {
          params.append('traderBalanceMin', String(traderBalanceMin));
        }
        if (traderBalanceMax != null) {
          params.append('traderBalanceMax', String(traderBalanceMax));
        }
        // Card-attribute filters on the card involved in each event (decision 9).
        if (filters) {
          appendCardFilterParams(params, filters);
        }
        return {
          url: `/activity/recent?${params.toString()}`,
          method: 'GET',
          requiresAuth: false,
        };
      },
      // Normalize the trader's country onto `countryCode` so the avatar flag renders
      // regardless of whether the backend DTO names the field `countryCode` or `country`
      // (other endpoints expose the raw `country` column). Mirrors the available-feed
      // `toActivity` fallback so both feeds behave identically. No country → null → no flag.
      transformResponse: (response: RecentActivityResponse): RecentActivityResponse => ({
        ...response,
        activities: (response.activities ?? []).map((activity) => ({
          ...activity,
          user: {
            ...activity.user,
            countryCode: activity.user?.countryCode ?? activity.user?.country ?? null,
          },
        })),
      }),
      // Collapse all paginated calls into a single cache entry — keyed by the balance
      // filter so filtered/unfiltered feeds don't share one cache slot (cursor is merged in).
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}_${queryArgs?.traderBalanceMin ?? ''}_${queryArgs?.traderBalanceMax ?? ''}_${filterKey(queryArgs?.filters)}`,
      // Merge paginated results into existing cache
      merge: (currentCache, newItems, { arg }) => {
        if (!arg.cursor) {
          // Fresh fetch (no cursor) = replace entire cache
          return newItems;
        }
        // Paginated fetch = append new items, deduplicate by ID
        const existingIds = new Set(currentCache.activities.map(a => a.id));
        const uniqueNewItems = newItems.activities.filter(a => !existingIds.has(a.id));
        currentCache.activities.push(...uniqueNewItems);
        currentCache.nextCursor = newItems.nextCursor;
        currentCache.hasMore = newItems.hasMore;
        currentCache.lookbackCapped = newItems.lookbackCapped;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.cursor !== previousArg?.cursor;
      },
      keepUnusedDataFor: 120,
    }),

    getAvailableFeed: builder.query<AvailableFeedResponse, AvailableFeedRequest>({
      // Backend owns the page size (AFFORDABLE_FEED_PAGE_SIZE / subsequent split), so we do NOT
      // send `limit` — only the cursor, lookback window, and balance filters.
      query: ({ hours = 24, cursor, traderBalanceMin, traderBalanceMax, filters } = {}) => {
        const params = new URLSearchParams();
        params.append('hours', String(hours));
        if (cursor) {
          params.append('cursor', cursor);
        }
        if (traderBalanceMin != null) {
          params.append('traderBalanceMin', String(traderBalanceMin));
        }
        if (traderBalanceMax != null) {
          params.append('traderBalanceMax', String(traderBalanceMax));
        }
        if (filters) {
          appendCardFilterParams(params, filters);
        }
        return {
          url: `/activity/available?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      transformResponse: (response: any): AvailableFeedResponse => {
        // Tolerate the backend's back-compat aliases (items/activities, totalItems/total).
        const items = response?.items ?? response?.activities ?? [];
        const safeItems = Array.isArray(items) ? items : [];
        return {
          items: safeItems,
          totalItems: response?.totalItems ?? response?.total ?? safeItems.length,
          lookbackHours: response?.lookbackHours ?? 24,
          nextCursor: response?.nextCursor ?? null,
          hasMore: response?.hasMore ?? false,
          lookbackCapped: response?.lookbackCapped ?? false,
        };
      },
      // Collapse all paginated calls into a single cache entry — keyed by the lookback window +
      // balance filter so filtered/unfiltered feeds don't share one cache slot (cursor is merged in).
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}_${queryArgs?.hours ?? ''}_${queryArgs?.traderBalanceMin ?? ''}_${queryArgs?.traderBalanceMax ?? ''}_${filterKey(queryArgs?.filters)}`,
      // Merge paginated results into existing cache (mirrors getActivityFeed).
      merge: (currentCache, newItems, { arg }) => {
        if (!arg.cursor) {
          // Fresh fetch (no cursor) = replace entire cache
          return newItems;
        }
        // Paginated fetch = append new items, deduplicate by ID (available_${wantEntryId}_0)
        const existingIds = new Set(currentCache.items.map(i => i.wantEntryId));
        const uniqueNewItems = newItems.items.filter(i => !existingIds.has(i.wantEntryId));
        currentCache.items.push(...uniqueNewItems);
        currentCache.totalItems = newItems.totalItems;
        currentCache.nextCursor = newItems.nextCursor;
        currentCache.hasMore = newItems.hasMore;
        currentCache.lookbackCapped = newItems.lookbackCapped;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.cursor !== previousArg?.cursor;
      },
      providesTags: ['AvailableFeed'],
      keepUnusedDataFor: 120,
    }),

    getActivityStats: builder.query<ActivityStats, void>({
      query: () => ({
        url: '/activity/stats',
        method: 'GET',
        requiresAuth: false,
      }),
      keepUnusedDataFor: 30,
    }),
  }),
});

export const {
  useGetRecentActivityQuery,
  useGetActivityFeedQuery,
  useGetAvailableFeedQuery,
  useGetActivityStatsQuery,
} = activityApi;

/**
 * Helper to prepend a new activity to the RTK Query cache
 * Used by WebSocket handler to update cache optimistically
 */
export const prependActivityToCache = (
  dispatch: any,
  newActivity: Activity,
  queryArgs: RecentActivityRequest = { limit: 54 }
) => {
  dispatch(
    activityApi.util.updateQueryData('getRecentActivity', queryArgs, (draft) => {
      // Prepend new activity
      draft.activities.unshift(newActivity);
      // Maintain max 54 activities (9 rows × 6 cards)
      if (draft.activities.length > 54) {
        draft.activities.pop();
      }
    })
  );
};

/**
 * Helper to prepend a new activity to the activity feed cache
 * Used by WebSocket handler - safe no-op when feed cache doesn't exist
 */
export const prependActivityToFeedCache = (
  dispatch: any,
  newActivity: Activity,
) => {
  dispatch(
    activityApi.util.updateQueryData(
      'getActivityFeed',
      // Must match serializeQueryArgs key (endpointName)
      undefined as unknown as RecentActivityRequest,
      (draft) => {
        if (!draft.activities.some(a => a.id === newActivity.id)) {
          draft.activities.unshift(newActivity);
        }
      }
    )
  );
};

/**
 * Helper to update activity stats cache from WebSocket event
 */
export const updateActivityStatsCache = (
  dispatch: any,
  stats: ActivityStats,
) => {
  dispatch(
    activityApi.util.updateQueryData('getActivityStats', undefined, (draft) => {
      draft.tradesLastHour = stats.tradesLastHour;
      draft.packagesShippedToday = stats.packagesShippedToday;
      draft.cardsTradedThisWeek = stats.cardsTradedThisWeek;
    })
  );
};
